import {
  Drawer,
  Box,
  IconButton,
  Stack,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import { ChevronLeft, ChevronRight, Settings, Info, Help } from "@mui/icons-material";
import { DrawerNavMenu } from "./DrawerNavMenu";
// import { ThemeSwitch } from "./ThemeSwitch";
import { Logo } from "../logo/Logo";
import { UserMenu } from "../auth/UserMenu";
import { useDrawer } from "src/utils/useDrawer";
import { useAuth } from "src/utils/useAuth";
import { useTheme } from "@mui/material";
import { useState } from "react";
import { Link } from "react-router-dom";

const DRAWER_WIDTH_EXPANDED = 256;
const DRAWER_WIDTH_COLLAPSED = 64;

/**
 * Desktop navigation drawer (permanent sidebar).
 *
 * Features:
 * - Collapsible/expandable
 * - Logo and app name at top
 * - Navigation menu items
 * - Theme toggle
 * - User profile at bottom
 */
export function DrawerDesktop() {
  const { isOpen, toggle } = useDrawer();
  const { isEnabled } = useAuth();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const openAppMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Drawer
        variant="permanent"
        open={isOpen}
        sx={{
          width: isOpen ? DRAWER_WIDTH_EXPANDED : DRAWER_WIDTH_COLLAPSED,
          flexShrink: 0,
          whiteSpace: "nowrap",
          boxSizing: "border-box",
          "& .MuiDrawer-paper": {
            width: isOpen ? DRAWER_WIDTH_EXPANDED : DRAWER_WIDTH_COLLAPSED,
            boxSizing: "border-box",
            overflowX: "hidden",
            transition: theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: isOpen
                ? theme.transitions.duration.enteringScreen
                : theme.transitions.duration.leavingScreen,
            }),
          },
        }}
      >
        <Stack flexGrow={1} ml={isOpen ? 1 : 0} mr={isOpen ? 0.5 : 0}>
          {/* Logo/Header */}
          <Box
            ml={isOpen ? 1 : 0}
            mt={0.5}
            mb={1.5}
            display="flex"
            justifyContent={isOpen ? "flex-start" : "center"}
          >
            <Logo height={64} dark={theme.palette.mode === "dark"} collapsed={!isOpen} />
          </Box>

          {/* Navigation Menu */}
          <DrawerNavMenu mr={isOpen ? 0.5 : 0} flexGrow={1} collapsed={!isOpen} />

          {/* Theme Switch - hidden until dark mode is fully supported */}
          {/* <ThemeSwitch collapsed={!isOpen} /> */}

          {/* User Menu (Auth0 enabled) or App Menu (Auth0 disabled) */}
          {isEnabled ? (
            <UserMenu collapsed={!isOpen} />
          ) : (
            <Box mt={2} mb={2.5}>
              <ListItemButton
                onClick={openAppMenu}
                sx={{
                  justifyContent: isOpen ? "initial" : "center",
                  px: 2,
                  py: 1,
                  minHeight: 48,
                  maxHeight: 48,
                }}
              >
                {isOpen && <ListItemText primary="App Menu" />}
                {isOpen && (
                  <ListItemIcon
                    sx={{
                      minWidth: "unset",
                      justifyContent: "center",
                    }}
                  >
                    <ChevronRight />
                  </ListItemIcon>
                )}
              </ListItemButton>

              <Menu
                id="app-menu"
                anchorEl={anchorEl}
                anchorOrigin={{
                  horizontal: "right",
                  vertical: "bottom",
                }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                sx={{
                  ml: 1.2,
                  mt: 1.8,
                }}
              >
                <MenuItem
                  component={Link}
                  to="/settings"
                  onClick={handleClose}
                  sx={{ height: 48, px: 2 }}
                >
                  <ListItemIcon sx={{ minWidth: 32, mr: 2 }}>
                    <Settings />
                  </ListItemIcon>
                  <ListItemText primary="Settings" />
                </MenuItem>
                <MenuItem
                  component={Link}
                  to="/getting-started"
                  onClick={handleClose}
                  sx={{ height: 48, px: 2 }}
                >
                  <ListItemIcon sx={{ minWidth: 32, mr: 2 }}>
                    <Info />
                  </ListItemIcon>
                  <ListItemText primary="Getting Started" />
                </MenuItem>
                <MenuItem
                  component="a"
                  href="https://insight.tveyes.com/help/"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleClose}
                  sx={{ height: 48, px: 2 }}
                >
                  <ListItemIcon sx={{ minWidth: 32, mr: 2 }}>
                    <Help />
                  </ListItemIcon>
                  <ListItemText primary="Help" />
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Stack>
      </Drawer>

      {/* Toggle Button (positioned outside drawer) */}
      <Box
        sx={{
          position: "fixed",
          left: isOpen ? DRAWER_WIDTH_EXPANDED : DRAWER_WIDTH_COLLAPSED,
          top: "20px",
          transform: "translate(-50%, 0)",
          zIndex: theme.zIndex.drawer,
          transition: theme.transitions.create("left", {
            easing: theme.transitions.easing.sharp,
            duration: isOpen
              ? theme.transitions.duration.enteringScreen
              : theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <IconButton
          onClick={toggle}
          sx={{
            width: 24,
            height: 24,
            boxShadow: 4,
            backgroundColor: "background.paper",
            border:
              theme.palette.mode === "light"
                ? undefined
                : `1px solid ${theme.palette.action.disabledBackground}`,
            "&:hover": {
              backgroundColor: "background.paper",
            },
          }}
        >
          {isOpen ? <ChevronLeft /> : <ChevronRight />}
        </IconButton>
      </Box>
    </>
  );
}
